import { z } from 'zod';

/**
 * Subdomain validation pattern
 * Rules:
 * - Lowercase letters, numbers, and hyphens only
 * - Cannot start or end with a hyphen
 * - No consecutive hyphens
 */
export const SUBDOMAIN_PATTERN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

export const SUBDOMAIN_ERROR_MESSAGE =
  'Subdomain must contain only lowercase letters, numbers, and hyphens (e.g., "myteam" or "property-search")';

/**
 * Zod schema for subdomain validation
 */
export const subdomainSchema = z
  .string()
  .transform((value) => value.trim().toLowerCase())
  .refine((value) => value === '' || SUBDOMAIN_PATTERN.test(value), {
    message: SUBDOMAIN_ERROR_MESSAGE,
  });

export type SubdomainValidationResult = {
  isValid: boolean;
  error?: string;
};

/**
 * Validates a subdomain string
 * @param subdomain The subdomain to validate
 * @returns Validation result with isValid flag and optional error message
 */
export const validateSubdomain = (subdomain: string): SubdomainValidationResult => {
  if (!subdomain || subdomain.trim() === '') {
    return { isValid: true }; // Empty is valid (optional field)
  }

  const normalized = subdomain.trim().toLowerCase();
  if (SUBDOMAIN_PATTERN.test(normalized)) {
    return { isValid: true };
  }

  return {
    isValid: false,
    error: SUBDOMAIN_ERROR_MESSAGE,
  };
};
